import React, { useState } from 'react';
import { useEmployees } from '../context/EmployeeContext';
import { Employee } from '../data/employeeData';
import SidebarPanel from './ui/SidebarPanel';

interface AddEmployeeSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (employee: Omit<Employee, 'id'>) => void;
}

const AddEmployeeSidebar: React.FC<AddEmployeeSidebarProps> = ({ isOpen, onClose, onAdd }) => {
  const { getDepartments, employees } = useEmployees();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: '',
    jobTitle: '',
    managerId: '',
    employeeStatus: 'Active' as Employee['employeeStatus'],
    hireDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    skills: '',
    projects: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('📝 Add Employee form submitted', { formData, isOpen });
    
    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.email.includes('@')) newErrors.email = 'Please enter a valid email';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';

    // Check for duplicate email
    if (employees.some(emp => emp.email.toLowerCase() === formData.email.toLowerCase())) {
      newErrors.email = 'An employee with this email already exists';
    }

    if (Object.keys(newErrors).length > 0) {
      console.log('❌ Form validation failed', newErrors);
      setErrors(newErrors);
      return;
    }
    
    console.log('✅ Form validation passed, creating employee...');

    // Generate initials
    const initials = formData.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);

    // Create employee object
    const newEmployee: Omit<Employee, 'id'> = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      department: formData.department,
      jobTitle: formData.jobTitle.trim(),
      managerId: formData.managerId || null,
      employeeStatus: formData.employeeStatus,
      lastActivity: 'Just joined',
      hireDate: new Date(formData.hireDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      initials,
      skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
      projects: formData.projects ? formData.projects.split(',').map(p => p.trim()).filter(Boolean) : []
    };

    console.log('👤 Adding new employee:', newEmployee);
    onAdd(newEmployee);
    console.log('✅ Employee added successfully');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      department: '',
      jobTitle: '',
      managerId: '',
      employeeStatus: 'Active',
      hireDate: new Date().toISOString().split('T')[0],
      skills: '',
      projects: ''
    });
    setErrors({});
    onClose();
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      department: '',
      jobTitle: '',
      managerId: '',
      employeeStatus: 'Active',
      hireDate: new Date().toISOString().split('T')[0],
      skills: '',
      projects: ''
    });
    setErrors({});
    onClose();
  };

  // Get potential managers (employees in the same or higher departments)
  const potentialManagers = employees.filter(emp => 
    emp.department === formData.department || 
    emp.jobTitle.includes('VP') || 
    emp.jobTitle.includes('CEO') ||
    emp.jobTitle.includes('Director')
  );

  const renderInput = (
    label: string,
    field: string,
    type: string = 'text',
    required: boolean = false,
    options?: string[] | { value: string; label: string }[],
    placeholder?: string
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'select' ? (
        <select
          value={formData[field as keyof typeof formData] as string}
          onChange={(e) => handleChange(field, e.target.value)}
          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white ${
            errors[field] ? 'border-red-300' : 'border-gray-300'
          }`}
        >
          <option value="">{placeholder || `Select ${label.toLowerCase()}`}</option>
          {options?.map(option => 
            typeof option === 'string' ? (
              <option key={option} value={option}>{option}</option>
            ) : (
              <option key={option.value} value={option.value}>{option.label}</option>
            )
          )}
        </select>
      ) : (
        <input
          type={type}
          value={formData[field as keyof typeof formData] as string}
          onChange={(e) => handleChange(field, e.target.value)}
          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
            errors[field] ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder={placeholder}
        />
      )}
      {errors[field] && <p className="mt-1 text-sm text-red-600">{errors[field]}</p>}
    </div>
  );

  return (
    <SidebarPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Employee"
      width="xl"
      headerColor="bg-teal-500"
      headerTextColor="text-white"
    >
      <div className="p-6 pb-24">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Information Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Full Name', 'name', 'text', true, undefined, 'John Doe')}
                {renderInput('Email Address', 'email', 'email', true, undefined, 'john.doe@theta.com')}
              </div>
            </div>

            {/* Job Information Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Job Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Department', 'department', 'select', true, getDepartments(), 'Select Department')}
                {renderInput('Job Title', 'jobTitle', 'text', true, undefined, 'Software Engineer')}
                {renderInput('Manager', 'managerId', 'select', false, 
                  potentialManagers.map(manager => ({ 
                    value: manager.id, 
                    label: `${manager.name} - ${manager.jobTitle}` 
                  })), 
                  'No Manager'
                )}
                {renderInput('Status', 'employeeStatus', 'select', false, [
                  'Active', 'Remote', 'In Office', 'On Leave'
                ])}
              </div>
            </div>

            {/* Employment Details Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Employment Details
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Hire Date', 'hireDate', 'date')}
              </div>
            </div>

            {/* Skills and Projects Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Skills and Projects
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Skills (comma-separated)', 'skills', 'text', false, undefined, 'JavaScript, React, Node.js')}
                {renderInput('Projects (comma-separated)', 'projects', 'text', false, undefined, 'CRM Platform, Mobile App')}
              </div>
            </div>
          </div>

          {/* Fixed Footer with Action Buttons */}
          <div className="fixed bottom-0 right-0 bg-white border-t border-gray-200 p-4 w-[40rem] flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              Add Employee
            </button>
          </div>
        </form>
      </div>
    </SidebarPanel>
  );
};

export default AddEmployeeSidebar;
